import HomeIcon from "@mui/icons-material/HomeOutlined";
import StatIcon from "@mui/icons-material/InsertChartOutlinedTwoTone";
import LogoutIcon from "@mui/icons-material/Logout";
import TableIcon from "@mui/icons-material/TableRowsOutlined";
import { Box, styled, useMediaQuery } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { ReactComponent as HBscanOpened } from '../../assets/images/svg/Group 26.svg';
import { ReactComponent as HBscanClosed } from '../../assets/images/svg/Group 29.svg';
import useSsoLogin from "../../hooks/useSsoLogin";
import { ERole } from "../../models/enums/role";
import { routes } from "../../routes";
import { default as auth, default as AuthService } from "../../services/auth/AuthService";

interface NavLinkProps {
    selected: boolean;
    isNotPhone: boolean;
}

interface NavItem {
    icon: ReactNode;
    label: string;
    path: string;
}

const Nav = styled(Link)<NavLinkProps>(({ selected, theme, isNotPhone }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'initial',
    marginBottom: '2vh',
    paddingLeft: isNotPhone ? '1.8vh' : '1.5vh',
    height: '50px',
    color: selected ? 'white' : theme.palette.primary.dark,
    backgroundColor: selected ? theme.palette.primary.main : 'transparent',
    borderRadius: '10px',
    width: '80%',
    fontSize: 15,
    textDecoration: 'none',
    '@media (min-width: 600px)': {
        '&:hover': {
            color: 'black',
            backgroundColor: theme.palette.action.hover,
            borderRadius: '10px',
            flexDirection: 'row',
            alignItems: 'center',
        },
    }
}));

const Navbar = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    borderRight: "2px solid #e8e8e8",
    height: "100%",
    width: "20%",
    minWidth: "6.5vh",
    maxWidth: "9vh",
    zIndex: 1000,
    transition: "all 0.3s ease",
    background: theme.palette.background.paper,
    '@media (min-width: 600px)': {
        "&:hover": {
            width: "30%",
            maxWidth: "240px",
        },
    }
}));

const labelStyle = {
    marginLeft: "10px",
    opacity: 0,
    transition: ' 0.5s',
};

const SideBar = () => {
    const isNotPhone = useMediaQuery("(min-height:600px) and (min-width:600px)");
    const { t } = useTranslation(["common"]);
    const userRole = auth.user?.role;

    const navs: NavItem[] = [
        {
            path: routes.home,
            icon: <HomeIcon style={{ fontSize: isNotPhone ? "3.3vh" : "2.3vh" }} />,
            label: t("sidebar.home"),
        },
        {
            path: routes.dataRoute.data,
            icon: <StatIcon style={{ fontSize: isNotPhone ? "3.3vh" : "2.3vh" }} />,
            label: t("sidebar.dashboard"),
        },
        ...(userRole !== ERole.Client && userRole !== ERole.LimitedClient && userRole !== ERole.Supervisor ? [{
            path: routes.tableRoute.table,
            icon: <TableIcon style={{ fontSize: isNotPhone ? "3.3vh" : "2.3vh" }} />,
            label: t("sidebar.datatable"),
        }] : []),
    ];
    const [isHovered, setIsHovered] = useState(false);
    const location = useLocation();
    const [isShowLabel, setisShowLabel] = useState(false);
    const { onSsoLogout } = useSsoLogin({});

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (isHovered && isNotPhone) {
            timer = setTimeout(() => setisShowLabel(true), 180);
        } else {
            setisShowLabel(false);
        }

        return () => clearTimeout(timer);
    }, [isHovered]);

    const isSelected = (current: string, to: string) => {
        if (to === "/") {
            return current === to;
        }
        return current.replaceAll("/", "").includes(to.replaceAll("/", ""));
    };

    const onDisconnect = async () => {
        AuthService.disconnect();
        await onSsoLogout();
        window.location.href = "/login";
    };

    return (
        <Navbar
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{ paddingTop: '2.6vh' }}
        >
            {isHovered && isNotPhone ? (<HBscanOpened width={'9vh'} style={{ marginBottom: '16vh' }} />)
                : (<HBscanClosed width={'3.8vh'} style={{ marginBottom: '16vh' }} />)}
            {navs.map((nav) => (
                <Nav
                    key={nav.path}
                    to={nav.path}
                    selected={isSelected(location.pathname, nav.path)}
                    isNotPhone={isNotPhone}
                >
                    {nav.icon}
                    {isShowLabel && <span style={{ ...labelStyle, opacity: isShowLabel ? 1 : 0, fontSize: '1.6vh' }}>{nav.label}</span>}
                </Nav>
            ))}
            <Nav
                to="#"
                selected={false}
                onClick={onDisconnect}
                style={{ marginTop: '32vh' }}
                isNotPhone={isNotPhone}
            >
                <LogoutIcon style={{ fontSize: '3.3vh' }} />
                {isShowLabel && <span style={{ ...labelStyle, opacity: isShowLabel ? 1 : 0, fontSize: '1.6vh' }}>{t("sidebar.disconnect")}</span>}
            </Nav>
        </Navbar>
    );
};

export default SideBar;